import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Chip, Container, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { SiteLayout } from '../components/SiteLayout';
import { useIndustryNews, useInuDigest } from '../api/marketing';
import { colors } from '../theme';

const MotionBox = motion.create(Box);

/**
 * Marketing intelligence → Industry News.
 *
 * Lives at /marketing/news. Reached via the "Industry News" card on the main
 * MarketingPage. Shows a curated spirits-industry digest: a headline takeaway
 * plus the stories that matter to HY, each with a "why it matters" line and a
 * link to the source. The public-source alternative to Mark Brown's INU.
 */
export function MarketingNewsPage() {
  const { data, isLoading, error } = useIndustryNews();
  const stories = data?.stories ?? [];
  const inu = useInuDigest();
  const inuStories = inu.data?.stories ?? [];

  return (
    <SiteLayout subtitle="Marketing Intelligence" showDesktopHint>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <MotionBox
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <Box sx={{ mb: 2 }}>
            <RouterLink
              to="/marketing"
              style={{
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: colors.textMuted,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              Back to Marketing
            </RouterLink>
          </Box>

          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: colors.gold,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            Industry News
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: { xs: 28, md: 38 },
              fontWeight: 700,
              color: colors.textPrimary,
              lineHeight: 1.1,
              mb: 1,
            }}
          >
            This week in spirits
          </Typography>
          <Typography sx={{ fontSize: 14, color: colors.textMuted, mb: 4, maxWidth: 720 }}>
            The day's drinks-industry press, curated down to what matters for a premium American
            whiskey brand — each story with a one-line read on why it matters to HY.
          </Typography>
        </MotionBox>

        {/* ── From Mark Brown's Industry News Update (the emailed newsletter) ── */}
        {inuStories.length > 0 ? (
          <Box>
            <Box sx={{ mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.gold,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  mb: 0.5,
                }}
              >
                From Mark Brown · Industry News Update
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontSize: { xs: 22, md: 28 },
                  fontWeight: 700,
                  color: colors.textPrimary,
                  lineHeight: 1.15,
                }}
              >
                The full industry brief
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: colors.textMuted, mt: 0.75, maxWidth: 720 }}>
                Every story from today's INU — trade press and analyst notes, hand-curated by Mark
                Brown. Shown in full; not filtered.
              </Typography>
            </Box>

            <Stack spacing={1.5}>
              {inuStories.map((s, i) => {
                const clickable = !!s.url;
                return (
                  <Paper
                    key={`inu-${i}`}
                    elevation={0}
                    {...(clickable
                      ? {
                          component: 'a' as const,
                          href: s.url ?? undefined,
                          target: '_blank',
                          rel: 'noopener noreferrer',
                        }
                      : {})}
                    sx={{
                      display: 'block',
                      p: 2.25,
                      border: `1px solid ${colors.border}`,
                      borderLeft: `3px solid ${colors.gold}`,
                      borderRadius: 2,
                      textDecoration: 'none',
                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                      ...(clickable
                        ? {
                            '&:hover': {
                              borderColor: '#d1d5db',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                            },
                          }
                        : {}),
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1.5}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                          <Typography
                            sx={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}
                          >
                            {s.headline}
                          </Typography>
                          {clickable ? (
                            <OpenInNewIcon
                              sx={{ fontSize: 14, color: colors.textMuted, flexShrink: 0 }}
                            />
                          ) : null}
                        </Stack>
                        {s.summary ? (
                          <Typography
                            sx={{
                              fontSize: 13.5,
                              color: colors.textSecondary,
                              mt: 0.5,
                              lineHeight: 1.5,
                            }}
                          >
                            {s.summary}
                          </Typography>
                        ) : null}
                      </Box>
                      {s.source ? (
                        <Chip
                          label={s.source}
                          size="small"
                          sx={{
                            flexShrink: 0,
                            maxWidth: 160,
                            bgcolor: '#f3f4f6',
                            color: colors.textMuted,
                            fontSize: 10.5,
                            fontWeight: 600,
                          }}
                        />
                      ) : null}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>

            {inu.data?.received_at ? (
              <Typography
                sx={{ fontSize: 11, color: colors.textMuted, mt: 1.5, letterSpacing: '0.02em' }}
              >
                {inu.data.story_count} stories · received{' '}
                {new Date(inu.data.received_at).toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        <Box sx={{ mt: 6 }}>
          {isLoading ? (
            <Stack spacing={1.5}>
              <Skeleton variant="rectangular" height={96} sx={{ borderRadius: 2 }} />
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rectangular" height={88} sx={{ borderRadius: 2 }} />
              ))}
            </Stack>
          ) : error ? (
            <Alert severity="error">Failed to load industry news: {(error as Error).message}</Alert>
          ) : data?.error || stories.length === 0 ? (
            <Alert severity="info">
              No curated stories available right now
              {data?.error ? ` (${data.error})` : ''}. Try again shortly.
            </Alert>
          ) : (
            <Stack spacing={2}>
              {/* Headline takeaway — the single biggest signal this week */}
              {data?.headline_takeaway ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, md: 3 },
                    bgcolor: '#fefcf6',
                    border: `1.5px solid ${colors.gold}40`,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: colors.gold,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      mb: 0.75,
                    }}
                  >
                    What it means for HY
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 16,
                      color: colors.textPrimary,
                      lineHeight: 1.55,
                      fontWeight: 500,
                    }}
                  >
                    {data.headline_takeaway}
                  </Typography>
                </Paper>
              ) : null}

              {/* Story list */}
              {stories.map((s, i) => (
                <Paper
                  key={`${s.url}-${i}`}
                  elevation={0}
                  component="a"
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'block',
                    p: 2.25,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 2,
                    textDecoration: 'none',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      borderColor: '#d1d5db',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1.5}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <Typography
                          sx={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}
                        >
                          {s.headline}
                        </Typography>
                        <OpenInNewIcon
                          sx={{ fontSize: 14, color: colors.textMuted, flexShrink: 0 }}
                        />
                      </Stack>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          color: colors.goldDark,
                          mt: 0.5,
                          fontStyle: 'italic',
                          lineHeight: 1.5,
                        }}
                      >
                        → {s.why_it_matters}
                      </Typography>
                    </Box>
                    {s.source ? (
                      <Chip
                        label={s.source}
                        size="small"
                        sx={{
                          flexShrink: 0,
                          bgcolor: '#f3f4f6',
                          color: colors.textMuted,
                          fontSize: 10.5,
                          fontWeight: 600,
                        }}
                      />
                    ) : null}
                  </Stack>
                </Paper>
              ))}

              {data?.generated_at ? (
                <Typography
                  sx={{ fontSize: 11, color: colors.textMuted, mt: 1, letterSpacing: '0.02em' }}
                >
                  Curated from {data.article_count} articles ·{' '}
                  {new Date(data.generated_at).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </Typography>
              ) : null}
            </Stack>
          )}
        </Box>
      </Container>
    </SiteLayout>
  );
}
